import React, { useState, useRef, useEffect } from "react";
import NewCommonModalCenter from "../../../components/common/NewCommonModalCenter";
import { IconUpload, IconPlus, IconX, IconTrash, IconFileText, IconCalendar } from "@tabler/icons-react";
import { fetchHrEmployees, fetchHrLeaveTypes, createHrLeaveRequest } from "../../../apis/hrApi";
import toast from "react-hot-toast";

type AddLeaveModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (newLeave: any) => void;
  isSidebar?: boolean;
};

const STATUS_OPTIONS = [
  { value: "Pending", text: "Pending" },
  { value: "Approved", text: "Approved" },
  { value: "Rejected", text: "Rejected" }
];

const SectionBlock = ({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: any;
  children: React.ReactNode;
}) => (
  <div className="group rounded-2xl bg-gradient-to-b from-white/90 to-white/60 backdrop-blur-xl border border-slate-200/50 shadow-sm p-4 transition-all duration-300 space-y-4 relative">
    <div className="absolute inset-0 rounded-2xl bg-gradient-to-tr from-yellow-200/10 to-transparent pointer-events-none" />
    <div className="flex items-center gap-2 border-b border-[#FBD037]/30 pb-2 mb-3 relative z-10">
      <div className="p-1.5 rounded-lg bg-[#FBD037]/20">
        <Icon size={16} className="text-[#212121]" stroke={1.5} />
      </div>
      <p className="text-[#212121]/80 uppercase text-[11px] font-bold tracking-wide">
        {title}
      </p>
    </div>
    <div className="relative z-10">
      {children}
    </div>
  </div>
);

interface InputFieldProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
}

const InputField = React.forwardRef<HTMLInputElement, InputFieldProps>(
  ({ label, placeholder, type = "text", className = "", error, required, ...rest }, ref) => {
    return (
      <div className={`w-full space-y-1 ${className}`}>
        <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <input
            ref={ref}
            type={type}
            placeholder={placeholder}
            {...rest}
            className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold"
          />
        </div>
        {error && <p className="text-red-500 text-xs">{error}</p>}
      </div>
    );
  }
);
InputField.displayName = "InputField";

const SelectInput = ({
  label,
  value,
  setValue,
  selectOptions = [],
  className = "",
  required,
  disabled,
}: {
  label: string;
  value?: string | number;
  setValue?: (val: string) => void;
  selectOptions?: { value: string | number; text: string }[] | false;
  className?: string;
  required?: boolean;
  disabled?: boolean;
}) => {
  return (
    <div className={`space-y-1 w-full ${className}`}>
      <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <div className="relative flex items-center w-full">
        <select
          value={value}
          onChange={(e) => setValue && setValue(e.target.value)}
          disabled={disabled}
          className="w-full rounded-xl py-[10px] px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed cursor-pointer"
        >
          <option value="">Select</option>
          {selectOptions &&
            selectOptions.map((opt, i) => (
              <option key={i} value={opt.value}>
                {opt.text}
              </option>
            ))}
        </select>
      </div>
    </div>
  );
};

interface TextareaFieldProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  error?: string;
}

const TextareaField = ({
  label,
  name,
  placeholder,
  rows = 3,
  value,
  onChange,
  disabled,
  ...rest
}: TextareaFieldProps) => {
  return (
    <div className="w-full space-y-1">
      {label && (
        <label className="text-[#1E293B] text-xs font-semibold flex items-center gap-0.5 mb-1 block">
          {label}
        </label>
      )}
      <textarea
        name={name}
        placeholder={placeholder}
        rows={rows}
        value={value}
        onChange={onChange}
        disabled={disabled}
        {...rest}
        className="w-full rounded-xl py-2 px-4 outline-none text-xs bg-white border border-[#D8D8D8] text-[#1E1E1E] focus:border-[#FBD037] focus:ring-2 focus:ring-[#FBD037]/20 transition-all font-semibold disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
      />
    </div>
  );
};

export default function AddLeaveModal({ isOpen, onClose, onSave, isSidebar = false }: AddLeaveModalProps) {
  const [durationType, setDurationType] = useState<"Full Day" | "Multiple" | "First Half" | "Second Half">("Full Day");
  const [selectedDate, setSelectedDate] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [employee, setEmployee] = useState("");
  const [leaveType, setLeaveType] = useState("Sick");
  const [status, setStatus] = useState("Pending");
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [employeeOptions, setEmployeeOptions] = useState<{ value: string; text: string; id: string; avatar?: string; dept?: string }[]>([]);
  const [leaveTypeOptions, setLeaveTypeOptions] = useState<{ value: string; text: string; id: string }[]>([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    const loadOptions = async () => {
      setIsLoadingOptions(true);
      try {
        const empRes = await fetchHrEmployees();
        if (empRes.success && empRes.data?.employees) {
          const formattedEmps = empRes.data.employees.map((emp: any) => ({
            value: emp.firstName + " " + emp.lastName,
            text: `${emp.firstName} ${emp.lastName} (${emp.departmentName || emp.department?.branchName || "-"})`,
            id: emp.id || emp._id,
            avatar: emp.profilePic || emp.avatar || "",
            dept: emp.departmentName || "-"
          }));
          setEmployeeOptions(formattedEmps);
        }

        const typeRes = await fetchHrLeaveTypes();
        if (typeRes.success) {
          const typesList = typeRes.data?.leaveTypes || typeRes.data || [];
          const formattedTypes = typesList.map((type: any) => ({
            value: type.leaveType,
            text: type.leaveType,
            id: type.id || type._id
          }));
          setLeaveTypeOptions(formattedTypes);
          if (formattedTypes.length > 0 && !leaveType) {
            setLeaveType(formattedTypes[0].value);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setIsLoadingOptions(false);
      }
    };

    loadOptions();
  }, [isOpen]);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      setFiles(prev => [...prev, ...selectedFiles].slice(0, 10)); // max 10 files
    }
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, idx) => idx !== index));
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      const droppedFiles = Array.from(e.dataTransfer.files);
      setFiles(prev => [...prev, ...droppedFiles].slice(0, 10));
    }
  };

  const handleSave = async () => {
    if (!employee) {
      toast.error("Please select an employee.");
      return;
    }
    if (durationType === "Multiple" && (!fromDate || !toDate)) {
      toast.error("Please select both From and To dates.");
      return;
    }
    if (durationType !== "Multiple" && !selectedDate) {
      toast.error("Please select a date.");
      return;
    }
    if (!reason.trim()) {
      toast.error("Please enter a reason for absence.");
      return;
    }

    const selectedEmp = employeeOptions.find(opt => opt.value === employee);
    const selectedType = leaveTypeOptions.find(opt => opt.value === leaveType);

    const payload = {
      leaveTypeId: selectedType?.id,
      leaveMode: durationType === "Full Day" ? "FULL_DAY" : durationType === "First Half" ? "FIRST_HALF" : durationType === "Second Half" ? "SECOND_HALF" : "MULTIPLE",
      startDate: durationType === "Multiple" ? fromDate : selectedDate,
      endDate: durationType === "Multiple" ? toDate : selectedDate,
      reason: reason,
    };

    try {
      const res = await createHrLeaveRequest(payload);
      if (res.success) {
        toast.success("Leave request submitted successfully");
        onSave(res.data);
        resetForm();
        onClose();
      } else {
        toast.error(res.message || "Failed to submit leave request");
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message || "Failed to submit leave request");
    }
  };

  const resetForm = () => {
    setDurationType("Full Day");
    setSelectedDate("");
    setFromDate("");
    setToDate("");
    setEmployee("");
    setLeaveType("Sick");
    setStatus("Pending");
    setReason("");
    setFiles([]);
  };

  const formContent = (
    <div className={isSidebar ? "bg-white p-5 max-h-[calc(100vh-160px)] overflow-y-auto space-y-4" : "bg-white rounded-b-3xl p-6 max-h-[90vh] overflow-y-auto space-y-4"}>
      
      {/* Leave Duration Type Radio Buttons */}
      <div className="flex flex-wrap items-center gap-3 mb-1 bg-slate-50 p-2.5 rounded-xl border border-slate-100">
        {(["Full Day", "Multiple", "First Half", "Second Half"] as const).map((type) => {
          const isSelected = durationType === type;
          return (
            <label
              key={type}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all cursor-pointer select-none ${
                isSelected
                  ? "bg-[#FBD037]/15 border-[#FBD037] text-[#212121]"
                  : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
              }`}
            >
              <input
                type="radio"
                name="durationType"
                checked={isSelected}
                onChange={() => setDurationType(type)}
                className="hidden"
              />
              <span className={`w-2 h-2 rounded-full transition-all ${isSelected ? "bg-[#212121]" : "bg-slate-300"}`} />
              <span>{type}</span>
            </label>
          );
        })}
      </div>

      <SectionBlock title="Leave Details" icon={IconFileText}>
        {/* Primary Input Fields Row 1 */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {durationType === "Multiple" ? (
            <div className="grid grid-cols-2 gap-3">
              <InputField
                label="From Date *"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
              <InputField
                label="To Date *"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
          ) : (
            <InputField
              label="Select Date *"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          )}

          <SelectInput
            label="Employee"
            required
            selectOptions={employeeOptions}
            value={employee}
            setValue={setEmployee}
          />
        </div>

        {/* Row 2: Leave Type & Status */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <SelectInput
                label="Leave Type"
                required
                selectOptions={leaveTypeOptions}
                value={leaveType}
                setValue={setLeaveType}
              />
            </div>
            <button
              type="button"
              className="h-[38px] w-[38px] bg-[#FBD037] text-[#212121] rounded-xl flex items-center justify-center flex-shrink-0 hover:bg-[#e2bd30] transition cursor-pointer border-none shadow-sm"
            >
              <IconPlus size={18} />
            </button>
          </div>

          <SelectInput
            label="Status"
            selectOptions={STATUS_OPTIONS}
            value={status}
            setValue={setStatus}
          />
        </div>

        {/* Textarea: Reason for Absence */}
        <div className="mt-3">
          <TextareaField
            label="Reason For Absence *"
            name="reason"
            placeholder="Enter Reason For Absence"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </SectionBlock>

      {/* Documents Upload Section */}
      <SectionBlock title="Attachment Documents" icon={IconUpload}>
        <div
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-200 hover:border-[#FBD037] rounded-2xl p-6 flex flex-col items-center justify-center bg-gray-50/50 hover:bg-amber-50/10 transition cursor-pointer"
        >
          <input
            type="file"
            ref={fileInputRef}
            multiple
            accept="image/*,application/pdf"
            onChange={handleFileChange}
            className="hidden"
          />
          
          <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shadow-xs border border-gray-100 text-gray-500 mb-2">
            <IconUpload size={18} />
          </div>

          <p className="text-xs font-bold text-gray-800 mb-0.5">Drag & drop or click to browse</p>
          <p className="text-[10px] text-gray-400 mb-0.5">Maximum 10 files • Max 3MB per file</p>
          <p className="text-[9px] text-gray-400">Accepted: image/*, application/pdf</p>
        </div>

        {/* Selected Files Preview List */}
        {files.length > 0 && (
          <div className="mt-3 space-y-2 max-h-48 overflow-y-auto p-1">
            {files.map((file, idx) => (
              <div key={idx} className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs">
                <div className="flex items-center gap-2 truncate">
                  <IconFileText size={16} className="text-gray-400 shrink-0" />
                  <span className="font-medium text-gray-700 truncate">{file.name}</span>
                  <span className="text-[10px] text-gray-400 shrink-0">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(idx);
                  }}
                  className="text-gray-400 hover:text-red-500 transition cursor-pointer"
                >
                  <IconTrash size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </SectionBlock>

      {/* Modal Footer Controls */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={() => {
            resetForm();
            onClose();
          }}
          className="px-5 py-2 border border-gray-200 rounded-xl text-xs font-semibold hover:bg-gray-50 text-gray-700 transition cursor-pointer bg-white"
        >
          Cancel
        </button>
        
        <button
          type="button"
          onClick={handleSave}
          className="px-6 py-2 bg-[#FBD037] text-[#212121] rounded-xl text-xs font-bold hover:bg-[#e2bd30] transition cursor-pointer border-none shadow-sm"
        >
          Save
        </button>
      </div>

    </div>
  );

  if (isSidebar) {
    return formContent;
  }

  return (
    <NewCommonModalCenter
      open={isOpen}
      onClose={() => {
        resetForm();
        onClose();
      }}
      title="Add New Leave"
      hideFooter={true}
      classWidth="w-11/12 max-w-3xl"
    >
      {formContent}
    </NewCommonModalCenter>
  );
}
